import { lazy, Suspense, useEffect, useState } from 'react';
import { Route, Routes, BrowserRouter } from 'react-router-dom';
import { ThemeProvider, ToastNotification, ToastNotificationProvider } from '@sendible/design-system';
import { lookup } from '@sendible/localization';
import queryClient from '@sendible/frontendv2/src/data-layer/queryClient';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { QueryClientProvider } from '@tanstack/react-query';
import Editor from '@sendible/frontendv2/src/components/CreativeEditor';
import { isAutomationEnvironment, storageUtils } from '@sendible/common';
import { faro } from '@grafana/faro-web-sdk';
import CoreFacade from './CoreFacade';
import setupFaroErrorReporting from './faroErrorReporting';

const Reconnections = lazy(() => import('@sendible/reconnections'));
const Signup = lazy(() => import('@sendible/signup').then((module) => ({ default: module.default as unknown as React.ComponentType })));
const SubscriptionPage = lazy(() => import('@sendible/subscriptionv2'));

if (!isAutomationEnvironment()) {
  setupFaroErrorReporting();
}

window.lookup = lookup;

function Orchestrator() {
  const [theme, setTheme] = useState<string | undefined>(undefined);
  const subscriptionParams = new URLSearchParams(window.location.search);

  const normalizeTheme = (t: string) => {
    const lc = t.toLowerCase();
    const normalizeUnderscore = lc.split('_').reduce((s, c) => s + (c.charAt(0).toUpperCase() + c.slice(1)));
    const normalizedThemeName = normalizeUnderscore.split('-').reduce((s, c) => s + (c.charAt(0).toUpperCase() + c.slice(1)));

    setTheme(normalizedThemeName);
  };

  useEffect(() => {
    if (Object.keys(faro).length > 0 && window?.$current_user?.id !== undefined) {
      faro.api.setUser({
        id: String(window?.$current_user?.id),
        email: window?.$current_user?.email || '',
        username: window?.$current_user?.fullname || '',
      });
    }
  }, [window?.$current_user?.id]);

  return (
    <QueryClientProvider client={queryClient}>
      <ReactQueryDevtools initialIsOpen={false} />
      <ThemeProvider theme={theme}>
        <Suspense fallback={<div />}>
          <ToastNotificationProvider>
            <BrowserRouter>
              <Routes>
                <Route
                  path="/subscription"
                  element={
                    <Suspense fallback={<div />}>
                      <SubscriptionPage
                        accessToken={subscriptionParams.get('access') || ''}
                        username={subscriptionParams.get('username') || ''}
                        timezone={subscriptionParams.get('tz') || ''}
                        language={subscriptionParams.get('lang') || 'en'}
                        addonFeatureEnabled={subscriptionParams.get('addon') === 'true'}
                        userRequestedImmediateCancellation={
                          storageUtils.getCookieOrLocalStorageItem(`set_cancelled_immediately_${subscriptionParams.get('id') || ''}`) === 'true'
                        }
                      />
                    </Suspense>
                  }
                />
                <Route
                  path="/editor/*"
                  element={
                    <Suspense fallback={<div />}>
                      <ToastNotification />
                      <Editor />
                    </Suspense>
                  }
                />
                <Route
                  path="/reconnections/*"
                  element={
                    <Suspense fallback={<div />}>
                      <Reconnections />
                    </Suspense>
                  }
                />
                <Route
                  path="/signup/*"
                  element={
                    <Suspense fallback={<div />}>
                      <Signup />
                    </Suspense>
                  }
                />
                <Route
                  path="/concise/*"
                  element={<CoreFacade onTheme={(t: string) => normalizeTheme(t)} />}
                />
                <Route
                  path="*"
                  element={<CoreFacade onTheme={(t) => normalizeTheme(t)} />}
                />
              </Routes>
            </BrowserRouter>
          </ToastNotificationProvider>
        </Suspense>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default Orchestrator;
